import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Loader2, FileText, FilterX } from 'lucide-react';
import { authHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { SearchableSelect } from '../components/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORMA_LABELS = { pix: 'PIX', dinheiro: 'Dinheiro', transferencia: 'Transferência', boleto: 'Boleto', cartao: 'Cartão' };
const GRUPO_LABELS = {
  receita_producao: 'Receita Produção', aporte_capital: 'Aporte Capital', venda_ativo: 'Venda Ativo',
  receita_nao_operacional: 'Receita Não Operacional',
  emprestimo_socio: 'Empréstimo de Sócio', emprestimo_bancario: 'Empréstimo Bancário',
  combustivel_maquinas: 'Combustível Máquinas', combustivel_veiculos: 'Combustível Veículos',
  lubrificacao: 'Lubrificação', insumos: 'Insumos', pecas: 'Peças', material_rodante: 'Material Rodante',
  manutencao_maquinas: 'Manutenção Máquinas', manutencao_veiculos: 'Manutenção Veículos',
  pessoal: 'Pessoal', deslocamento: 'Deslocamento', campo_operacao: 'Campo/Operação',
  saude: 'Saúde', administrativo: 'Administrativo', controle_interno: 'Controle Interno', residual: 'Residual',
};

// Bloco 8 — Naturezas com cor + tipo padrão + grupos filtrados
const NATUREZAS = [
  { id: 'N1', label: 'N1 — Receita Operacional', color: 'emerald', tipo: 'entrada', grupos: ['receita_producao'] },
  { id: 'N2', label: 'N2 — Receita Não Operacional', color: 'yellow', tipo: 'entrada', grupos: ['venda_ativo', 'receita_nao_operacional'] },
  { id: 'N3', label: 'N3 — Aporte de Sócio', color: 'blue', tipo: 'entrada', grupos: ['aporte_capital'] },
  { id: 'N4s', label: 'N4 — Empréstimo de Sócio', color: 'purple', tipo: 'entrada', grupos: ['emprestimo_socio'], emprestimo: true },
  { id: 'N4b', label: 'N4 — Empréstimo Bancário', color: 'fuchsia', tipo: 'entrada', grupos: ['emprestimo_bancario'], emprestimo: true },
  { id: 'N5', label: 'N5 — Despesa Direta / Operacional', color: 'red', tipo: 'saida', grupos: null /* qualquer saída */ },
];
const NATUREZA_COLORS = {
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
  fuchsia: 'bg-fuchsia-500/10 border-fuchsia-500/30 text-fuchsia-400',
  red: 'bg-red-500/10 border-red-500/30 text-red-400',
};

function natureFromGrupo(grupo) {
  return NATUREZAS.find(n => n.grupos && n.grupos.includes(grupo)) || NATUREZAS[NATUREZAS.length - 1];
}

function getMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return { data_inicio: `${y}-${m}-01`, data_fim: `${y}-${m}-${new Date(y, now.getMonth() + 1, 0).getDate()}` };
}

const EMPTY_FORM = {
  data: new Date().toISOString().slice(0, 10), tipo: 'saida', categoria_id: '', valor: '',
  forma_pagamento: 'pix', tipo_destino: '', equipamento_id: '', operacao_id: '',
  local_operacao_id: '', responsavel_id: '', observacao: '',
  // Bloco 8 — campos para empréstimos
  fornecedor_id: '', numero_parcelas: '', data_primeiro_vencimento: '', juros_percentual_mensal: '',
  // Bloco 9 — mês de referência (pagamento de salário)
  mes_referencia: '',
};

export default function FinanceiroLancamentos() {
  const { user } = useAuth();
  const isMaster = user?.is_master === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [lancamentos, setLancamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [operators, setOperators] = useState([]);
  const [locations, setLocations] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [naturezaSel, setNaturezaSel] = useState(null);  // Bloco 8 — natureza escolhida no form
  // Filtros — inicializados a partir da URL (persistência ao navegar e voltar)
  const [periodo, setPeriodo] = useState(() => {
    const di = searchParams.get('di');
    const df = searchParams.get('df');
    return di && df ? { data_inicio: di, data_fim: df } : getMonthRange();
  });
  const [filterTipo, setFilterTipo] = useState(() => searchParams.get('tipo') || '');
  const [filterCat, setFilterCat] = useState('');
  const [filterGrupo, setFilterGrupo] = useState(() => searchParams.get('grupo') || '');
  const [filterLocal, setFilterLocal] = useState(() => searchParams.get('local') || '');
  const [filterColaborador, setFilterColaborador] = useState(() => searchParams.get('colab') || '');
  const [filterAtivo, setFilterAtivo] = useState(() => searchParams.get('ativo') || '');
  const [filterFornecedor, setFilterFornecedor] = useState(() => searchParams.get('forn') || '');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Sincroniza os filtros aplicados na URL (persistência entre navegações)
  useEffect(() => {
    const p = { di: periodo.data_inicio, df: periodo.data_fim };
    if (filterTipo) p.tipo = filterTipo;
    if (filterGrupo) p.grupo = filterGrupo;
    if (filterLocal) p.local = filterLocal;
    if (filterColaborador) p.colab = filterColaborador;
    if (filterAtivo) p.ativo = filterAtivo;
    if (filterFornecedor) p.forn = filterFornecedor;
    setSearchParams(p, { replace: true });
  }, [periodo, filterTipo, filterGrupo, filterLocal, filterColaborador, filterAtivo, filterFornecedor, setSearchParams]);

  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    let qs = `data_inicio=${periodo.data_inicio}&data_fim=${periodo.data_fim}`;
    if (filterTipo) qs += `&tipo=${filterTipo}`;
    if (filterCat) qs += `&categoria_id=${filterCat}`;
    if (filterLocal) qs += `&local_operacao_id=${filterLocal}`;
    if (filterColaborador) qs += `&responsavel_id=${filterColaborador}`;
    if (filterAtivo) qs += `&equipamento_id=${filterAtivo}`;
    if (filterFornecedor) qs += `&fornecedor_id=${filterFornecedor}`;
    // fetch resiliente: falha/timeout de um endpoint não derruba o carregamento dos demais
    const safeJson = async (url) => {
      try {
        const r = await fetch(url, { headers: authHeaders(), signal });
        return r.ok ? await r.json() : null;
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('Falha ao carregar', url, e.message);
        return null;
      }
    };
    try {
      const [lD, cD, oD, locD, colD, fD] = await Promise.all([
        safeJson(`${API_URL}/api/financeiro/lancamentos?${qs}`),
        safeJson(`${API_URL}/api/financeiro/plano-de-contas`),
        safeJson(`${API_URL}/api/operators`),
        safeJson(`${API_URL}/api/locations`),
        safeJson(`${API_URL}/api/colaboradores/options`),
        safeJson(`${API_URL}/api/fornecedores`),
      ]);
      if (lD) setLancamentos(lD);
      if (cD) setCategorias(cD);
      if (oD) setOperators(oD);
      if (locD) setLocations(locD);
      if (colD) setColaboradores(colD);
      if (fD) setFornecedores(fD);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
    // Equipamentos (destino): independente — nunca bloqueia Categoria/Local
    safeJson(`${API_URL}/api/financeiro/equipamentos-options`).then(eD => { if (eD) setEquipamentos(eD); });
  }, [periodo, filterTipo, filterCat, filterLocal, filterColaborador, filterAtivo, filterFornecedor]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const selectedCat = categorias.find(c => c.id === form.categoria_id);
  const filteredByGrupo = filterGrupo ? lancamentos.filter(l => l.categoria_grupo === filterGrupo) : lancamentos;

  // Agrupa as opções de equipamento por categoria (Máquinas e Veículos, Equipamento, Imóvel, etc.)
  const equipamentosPorCategoria = React.useMemo(() => {
    const grupos = {};
    for (const e of equipamentos) {
      const k = e.categoria_label || 'Outros';
      (grupos[k] = grupos[k] || []).push(e);
    }
    return Object.entries(grupos).sort((a, b) => a[0].localeCompare(b[0]));
  }, [equipamentos]);

  // === Opções dos filtros granulares (autocomplete por nome) ===
  const colaboradorOptions = React.useMemo(() => {
    const colabs = colaboradores.map(c => ({ value: c.id, label: c.cargo ? `${c.nome} — ${c.cargo}` : c.nome }));
    const ops = operators.filter(o => o.status === 'active').map(o => ({ value: o.id, label: `${o.name} (Operador)` }));
    return [...colabs, ...ops];
  }, [colaboradores, operators]);

  const ativoOptions = React.useMemo(() =>
    equipamentos.map(e => ({ value: e.id, label: `${e.label}${e.categoria_label ? ` · ${e.categoria_label}` : ''}` })),
  [equipamentos]);

  const fornecedorOptions = React.useMemo(() =>
    fornecedores.map(f => ({ value: f.id, label: f.documento ? `${f.nome} — ${f.documento}` : f.nome })),
  [fornecedores]);

  const localOptions = React.useMemo(() =>
    locations.map(l => ({ value: l.id, label: `${l.name} - ${l.city}/${l.state}` })),
  [locations]);

  const hasActiveFilters = !!(filterTipo || filterGrupo || filterLocal || filterColaborador || filterAtivo || filterFornecedor);
  const clearAllFilters = () => {
    setFilterTipo(''); setFilterGrupo(''); setFilterLocal('');
    setFilterColaborador(''); setFilterAtivo(''); setFilterFornecedor('');
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNaturezaSel(null);
    setShowForm(true);
  };
  const openEdit = (l) => {
    setEditingId(l.id);
    const nat = natureFromGrupo(l.categoria_grupo);
    setNaturezaSel(nat);
    setForm({
      data: l.data, tipo: l.tipo, categoria_id: l.categoria_id, valor: String(l.valor),
      forma_pagamento: l.forma_pagamento, tipo_destino: l.tipo_destino || '',
      equipamento_id: l.equipamento_id || '', operacao_id: l.operacao_id || '',
      local_operacao_id: l.local_operacao_id || '',
      responsavel_id: l.responsavel_id || '', observacao: l.observacao || '',
      fornecedor_id: l.fornecedor_id || '',
      numero_parcelas: '', data_primeiro_vencimento: '', juros_percentual_mensal: '',
      mes_referencia: l.mes_referencia || '',
    });
    setShowForm(true);
  };

  // Lista de categorias filtrada pela natureza escolhida
  const categoriasFiltradas = (() => {
    const tipoAlvo = naturezaSel?.tipo || form.tipo;
    let lista = categorias.filter(c => c.ativo && c.tipo === tipoAlvo);
    if (naturezaSel?.grupos) {
      lista = lista.filter(c => naturezaSel.grupos.includes(c.grupo));
    }
    return lista;
  })();

  // Auto-selecionar categoria única após escolher natureza
  useEffect(() => {
    if (!naturezaSel) return;
    // Sempre força tipo + reseta categoria quando muda a natureza
    setForm(f => ({
      ...f,
      tipo: naturezaSel.tipo,
      categoria_id: categoriasFiltradas.length === 1 ? categoriasFiltradas[0].id : '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturezaSel]);

  const _doPost = async (url, payload) => {
    const r = await fetch(url, {
      method: editingId ? 'PATCH' : 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    const d = await r.json().catch(() => ({}));
    return { r, d };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.categoria_id) { toast.error('Selecione uma categoria'); return; }
    if (!form.valor || isNaN(form.valor) || Number(form.valor) <= 0) { toast.error('Valor inválido'); return; }

    const isEmprestimo = naturezaSel?.emprestimo === true && !editingId;
    if (isEmprestimo) {
      if (!form.fornecedor_id) { toast.error('Selecione o credor (sócio ou banco)'); return; }
      if (!form.numero_parcelas || Number(form.numero_parcelas) < 1) {
        toast.error('Informe o número de parcelas'); return;
      }
      if (!form.data_primeiro_vencimento) {
        toast.error('Informe a data do primeiro vencimento'); return;
      }
    }

    setSaving(true);
    const payload = { ...form, valor: Number(form.valor) };
    if (isEmprestimo) {
      payload.numero_parcelas = Number(form.numero_parcelas);
      payload.juros_percentual_mensal = Number(form.juros_percentual_mensal || 0);
    } else {
      // Não enviar campos de empréstimo se não aplicável
      delete payload.numero_parcelas;
      delete payload.data_primeiro_vencimento;
      delete payload.juros_percentual_mensal;
      if (!isEmprestimo) delete payload.fornecedor_id;
    }
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

    try {
      const baseUrl = editingId
        ? `${API_URL}/api/financeiro/lancamentos/${editingId}`
        : `${API_URL}/api/financeiro/lancamentos`;

      let { r, d } = await _doPost(baseUrl, payload);

      // Detecção de duplicidade (apenas na criação)
      if (r.ok && !editingId && d?.duplicate_alert?.has_duplicates) {
        const sample = (d.duplicate_alert.duplicates || []).slice(0, 3)
          .map(x => `• R$ ${Number(x.valor).toFixed(2)} em ${x.data}${x.fornecedor_nome ? ' — ' + x.fornecedor_nome : ''}`)
          .join('\n');
        const ok = window.confirm(
          `⚠️ Possível duplicidade detectada (${d.duplicate_alert.count} lançamento(s) semelhante(s)):\n\n${sample}\n\nDeseja registrar mesmo assim?`,
        );
        if (!ok) { setSaving(false); return; }
        const retry = await _doPost(`${baseUrl}?ignorar_duplicidade=true`, payload);
        r = retry.r; d = retry.d;
      }

      if (r.ok) {
        const msg = isEmprestimo
          ? `Empréstimo registrado — ${Number(form.numero_parcelas)} parcelas geradas no Contas a Pagar`
          : (editingId ? 'Lançamento atualizado' : 'Lançamento criado');
        toast.success(msg);
        setShowForm(false); setEditingId(null); setNaturezaSel(null); fetchData();
      } else {
        toast.error(d.detail || 'Erro ao salvar');
      }
    } catch { toast.error('Erro de conexão'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este lançamento?')) return;
    try {
      const r = await fetch(`${API_URL}/api/financeiro/lancamentos/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) { toast.success('Excluído'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const formatDate = (d) => { try { const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y}`; } catch { return d; } };

  const totalEntradas = filteredByGrupo.filter(l => l.tipo === 'entrada').reduce((s, l) => s + l.valor, 0);
  const totalSaidas = filteredByGrupo.filter(l => l.tipo === 'saida').reduce((s, l) => s + l.valor, 0);

  return (
    <div className="space-y-6" data-testid="financeiro-lancamentos">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Lançamentos</h1>
          <p className="text-sm text-gray-500">{filteredByGrupo.length} registros</p>
        </div>
        <button onClick={openNew} data-testid="btn-novo-lancamento"
          className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors text-sm flex items-center gap-2">
          <Plus size={16} /> Novo Lançamento
        </button>
      </div>

      {/* Filtros */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2 items-center">
          <input type="date" value={periodo.data_inicio} onChange={e => setPeriodo(p => ({ ...p, data_inicio: e.target.value }))}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="lanc-date-start" />
          <input type="date" value={periodo.data_fim} onChange={e => setPeriodo(p => ({ ...p, data_fim: e.target.value }))}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="lanc-date-end" />
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="lanc-filter-tipo">
            <option value="">Todos os tipos</option>
            <option value="entrada">Entradas</option>
            <option value="saida">Saídas</option>
          </select>
          <select value={filterGrupo} onChange={e => setFilterGrupo(e.target.value)}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="lanc-filter-grupo">
            <option value="">Todos os grupos</option>
            {Object.entries(GRUPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {/* Filtros granulares (autocomplete por nome) — independentes e combináveis (lógica E) */}
        <div className="flex flex-wrap gap-2 items-center">
          <SearchableSelect testId="lanc-filter-colaborador" value={filterColaborador} onChange={setFilterColaborador}
            options={colaboradorOptions} placeholder="Colaborador" />
          <SearchableSelect testId="lanc-filter-ativo" value={filterAtivo} onChange={setFilterAtivo}
            options={ativoOptions} placeholder="Centro de Custo (Ativo)" />
          <SearchableSelect testId="lanc-filter-fornecedor" value={filterFornecedor} onChange={setFilterFornecedor}
            options={fornecedorOptions} placeholder="Fornecedor" />
          <SearchableSelect testId="lanc-filter-local" value={filterLocal} onChange={setFilterLocal}
            options={localOptions} placeholder="Local de Operação" />
          {hasActiveFilters && (
            <button onClick={clearAllFilters} data-testid="lanc-clear-filters"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-red-400 border border-gray-700 hover:border-red-500/40 rounded-lg transition-colors">
              <FilterX size={14} /> Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Totais */}
      <div className="flex gap-4 text-sm">
        <span className="text-emerald-400">Entradas: R$ {totalEntradas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
        <span className="text-red-400">Saídas: R$ {totalSaidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
        <span className={totalEntradas - totalSaidas >= 0 ? 'text-emerald-400' : 'text-red-400'}>
          Saldo: R$ {(totalEntradas - totalSaidas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>

      {/* Modal/Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center pt-10 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-[#111] border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 mb-10" onClick={e => e.stopPropagation()} data-testid="form-lancamento">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">{editingId ? 'Editar' : 'Novo'} Lançamento</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              {/* Bloco 8 — Seletor de Natureza */}
              {!editingId && (
                <div data-testid="natureza-picker">
                  <label className="text-xs text-gray-400 mb-1 block">Natureza do lançamento</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {NATUREZAS.map(n => (
                      <button
                        type="button"
                        key={n.id}
                        data-testid={`natureza-${n.id}`}
                        onClick={() => setNaturezaSel(n)}
                        className={`text-left text-xs px-3 py-2 rounded-lg border transition-colors ${
                          naturezaSel?.id === n.id
                            ? NATUREZA_COLORS[n.color] + ' ring-1 ring-current'
                            : 'bg-[#0A0A0A] border-gray-800 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        {n.label}
                      </button>
                    ))}
                  </div>
                  {naturezaSel?.emprestimo && (
                    <p className="text-xs text-purple-300 mt-2">
                      🔔 Empréstimo gera parcelas no Contas a Pagar com o credor selecionado (Price — juros compostos mensais).
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Data</label>
                  <input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Tipo</label>
                  <select value={form.tipo}
                    onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                    disabled={!!naturezaSel}
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none disabled:opacity-60">
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Categoria</label>
                <select value={form.categoria_id} onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))} data-testid="select-categoria"
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                  <option value="">Selecione...</option>
                  {categoriasFiltradas.map(c => (
                    <option key={c.id} value={c.id}>{c.nome} ({GRUPO_LABELS[c.grupo] || c.grupo})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Valor (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none" placeholder="0,00" data-testid="lanc-valor" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Forma Pgto</label>
                  <select value={form.forma_pagamento} onChange={e => setForm(f => ({ ...f, forma_pagamento: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                    {Object.entries(FORMA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>

              {/* Bloco 8 — campos de Empréstimo (Sócio/Bancário) */}
              {naturezaSel?.emprestimo && !editingId && (
                <div className="space-y-3 p-3 border border-purple-500/30 rounded-lg bg-purple-500/5" data-testid="emprestimo-fields">
                  <p className="text-xs text-purple-300 font-medium">Dados do empréstimo (Price — juros compostos)</p>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">
                      Credor ({naturezaSel.id === 'N4s' ? 'sócio — PF' : 'banco / empresa — PJ'}) <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={form.fornecedor_id}
                      onChange={e => setForm(f => ({ ...f, fornecedor_id: e.target.value }))}
                      data-testid="emprestimo-fornecedor"
                      className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 outline-none"
                    >
                      <option value="">Selecione o credor...</option>
                      {fornecedores
                        .filter(f => (naturezaSel.id === 'N4s' ? f.tipo_pessoa === 'PF' : true))
                        .map(f => (
                          <option key={f.id} value={f.id}>
                            {f.nome}{f.documento ? ` — ${f.documento}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Nº Parcelas *</label>
                      <input type="number" min="1" max="360"
                        value={form.numero_parcelas}
                        onChange={e => setForm(f => ({ ...f, numero_parcelas: e.target.value }))}
                        data-testid="emprestimo-parcelas"
                        className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">1º Venc. *</label>
                      <input type="date"
                        value={form.data_primeiro_vencimento}
                        onChange={e => setForm(f => ({ ...f, data_primeiro_vencimento: e.target.value }))}
                        data-testid="emprestimo-data-1venc"
                        className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Juros % a.m.</label>
                      <input type="number" min="0" max="100" step="0.01"
                        value={form.juros_percentual_mensal}
                        onChange={e => setForm(f => ({ ...f, juros_percentual_mensal: e.target.value }))}
                        data-testid="emprestimo-juros"
                        placeholder="0,00"
                        className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                  </div>
                  {form.valor && form.numero_parcelas > 0 && (() => {
                    const v = Number(form.valor) || 0;
                    const n = Number(form.numero_parcelas) || 1;
                    const i = (Number(form.juros_percentual_mensal) || 0) / 100;
                    let pmt;
                    if (i <= 0) pmt = v / n;
                    else { const f = Math.pow(1 + i, n); pmt = v * i * f / (f - 1); }
                    const total = pmt * n;
                    return (
                      <div className="text-xs text-purple-200 bg-purple-500/10 rounded p-2" data-testid="emprestimo-preview">
                        Parcela ≈ <strong>R$ {pmt.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        {' • '}Total ≈ R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {i > 0 && ` (juros: R$ ${(total - v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`}
                      </div>
                    );
                  })()}
                </div>
              )}
              {(selectedCat?.requer_destino || selectedCat?.grupo === 'combustivel_maquinas' || selectedCat?.grupo === 'combustivel_veiculos' || selectedCat?.grupo === 'material_rodante') && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Equipamento (destino){(selectedCat?.grupo === 'combustivel_maquinas' || selectedCat?.grupo === 'combustivel_veiculos' || selectedCat?.grupo === 'material_rodante') && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <select value={form.equipamento_id} onChange={e => setForm(f => ({ ...f, equipamento_id: e.target.value }))} data-testid="select-equipamento"
                    className={`w-full bg-[#0A0A0A] border rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none ${(selectedCat?.grupo === 'combustivel_maquinas' || selectedCat?.grupo === 'combustivel_veiculos' || selectedCat?.grupo === 'material_rodante') && !form.equipamento_id ? 'border-red-500/50' : 'border-gray-700'}`}>
                    <option value="">Selecione...</option>
                    {equipamentosPorCategoria.map(([cat, opts]) => (
                      <optgroup key={cat} label={cat}>
                        {opts.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}

              {(selectedCat?.nome === 'Cantina' || (selectedCat?.requer_destino && selectedCat?.grupo === 'campo_operacao')) && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Operação (destino)<span className="text-red-400 ml-1">*</span>
                  </label>
                  <select value={form.operacao_id} onChange={e => setForm(f => ({ ...f, operacao_id: e.target.value, tipo_destino: 'operacao_geral' }))} data-testid="select-operacao"
                    className={`w-full bg-[#0A0A0A] border rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none ${!form.operacao_id ? 'border-red-500/50' : 'border-gray-700'}`}>
                    <option value="">Selecione...</option>
                    {locations.filter(l => l.status === 'active').map(l => (
                      <option key={l.id} value={l.id}>{l.full_name || l.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedCat?.requer_responsavel && (
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Responsável (obrigatório)</label>
                  <select value={form.responsavel_id} onChange={e => setForm(f => ({ ...f, responsavel_id: e.target.value }))} data-testid="select-responsavel"
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                    <option value="">Selecione...</option>
                    {colaboradores.length > 0 && (
                      <optgroup label="Colaboradores">
                        {colaboradores.map(c => (
                          <option key={`c-${c.id}`} value={c.id}>
                            {c.nome}{c.cargo ? ` — ${c.cargo}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {operators.filter(o => o.status === 'active').length > 0 && (
                      <optgroup label="Operadores de Máquina">
                        {operators.filter(o => o.status === 'active').map(o => (
                          <option key={`o-${o.id}`} value={o.id}>{o.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Busca dupla: cadastre pessoas em "Colaboradores" ou selecione um operador existente.</p>
                </div>
              )}

              {/* Bloco 9 — Mês de Referência (pagamento de Salário baixa a competência na CC) */}
              {selectedCat?.nome === 'Salários' && (
                <div data-testid="lanc-mes-referencia-block">
                  <label className="text-xs text-gray-400 mb-1 block">Mês de Referência (competência)</label>
                  <input type="month" value={form.mes_referencia}
                    onChange={e => setForm(f => ({ ...f, mes_referencia: e.target.value }))}
                    data-testid="input-mes-referencia"
                    className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none" />
                  <p className="text-xs text-gray-500 mt-1">
                    Informe a competência paga. O sistema baixa o crédito de salário mensal correspondente na Conta Corrente, zerando o período.
                  </p>
                </div>
              )}

              {/* Local de Operação — sempre disponível, opcional */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Local de Operação (opcional)</label>
                <select value={form.local_operacao_id} onChange={e => setForm(f => ({ ...f, local_operacao_id: e.target.value }))} data-testid="select-local-operacao"
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                  <option value="">— Sem vínculo —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name} - {l.city}/{l.state}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Vincule este lançamento a um Local de Operação para análises segmentadas.</p>
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-1 block">Observação (opcional)</label>
                <textarea value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} rows={2}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none resize-none" placeholder="Texto livre..." />
              </div>

              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={saving} data-testid="btn-salvar-lancamento"
                  className="flex-1 px-4 py-2.5 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 text-sm flex items-center justify-center gap-2">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null} {editingId ? 'Atualizar' : 'Salvar'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 text-sm">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
      ) : filteredByGrupo.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <FileText size={40} className="mx-auto mb-3 opacity-50" />
          <p>Nenhum lançamento no período</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredByGrupo.map(l => (
            <div key={l.id} data-testid={`lancamento-${l.id}`}
              className={`bg-[#111] border rounded-xl px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${l.origem !== 'manual' ? 'border-blue-500/20' : 'border-gray-800'}`}>
              <span className="text-sm text-gray-500 w-20">{formatDate(l.data)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{l.categoria_nome || 'Sem categoria'}</p>
                <p className="text-xs text-gray-500 truncate">
                  {GRUPO_LABELS[l.categoria_grupo] || l.categoria_grupo}
                  {l.equipamento_info && ` | ${l.equipamento_info.nome || `${l.equipamento_info.brand || ''} ${l.equipamento_info.model || ''}`.trim()}`}
                  {l.local_operacao_info && ` | 📍 ${l.local_operacao_info.name}`}
                  {l.responsavel_info && ` | ${l.responsavel_info.name}`}
                  {l.fornecedor_info && ` | 🏷️ ${l.fornecedor_info.nome}`}
                  {l.os_numero && ` | 🔧 O.S. #${l.os_numero}`}
                  {l.observacao && ` | ${l.observacao}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {l.origem !== 'manual' && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30">Auto</span>
                )}
                <span className={`text-sm font-mono font-semibold ${l.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {l.tipo === 'entrada' ? '+' : '-'} R$ {l.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-xs text-gray-600">{FORMA_LABELS[l.forma_pagamento] || l.forma_pagamento}</span>
                {(l.origem === 'manual' || isMaster) && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(l)} className="p-1.5 text-gray-500 hover:text-yellow-500 transition-colors" data-testid={`edit-${l.id}`}><Edit size={14} /></button>
                    <button onClick={() => handleDelete(l.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-${l.id}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
